import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/apiFetch';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import {
  Database,
  FileText,
  XCircle,
  AlertTriangle,
  Clock,
  TrendingUp,
  Loader2,
  RefreshCw,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Shield,
  Globe2,
  Lock,
} from 'lucide-react';
import AppNavActions from '../components/AppNavActions';
import { IscBadge } from '../components/badges';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface KpiData {
  totalRecords: number;
  confirmedLast30: number;
  pendingReview: number;
  failedIngestions: number;
  discardRate: number;
  piiDetected: number;
  maoriDataDetected: number;
  restrictedAccess: number;
}

interface DailyActivity {
  date: string;
  confirmed: number;
  discarded: number;
  failed: number;
  pending: number;
}

interface CategoryCount {
  name: string;
  value: number;
}

interface RecentItem {
  id: string;
  fileName: string;
  status: string;
  initiatedAt: Date;
  fileSize: number;
  contentType: string;
}

interface RecentRecord {
  id: string;
  title: string;
  status: string;
  createdAt: Date;
  fileSize: number;
  contentType: string;
  piiDetected: boolean;
  sensitivityLevel: string;
  maoriDataDetected: boolean;
  informationSecurityClassification: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BRAND_BG = 'bg-emerald-800';
const BRAND_TEXT = 'text-emerald-800';

const STATUS_COLORS: Record<string, string> = {
  confirmed: '#10B981',
  discarded: '#F87171',
  failed: '#F59E0B',
  pending: '#818CF8',
  draft: '#60A5FA',
  final: '#10B981',
  transitory: '#F59E0B',
  pending_finalisation: '#818CF8',
  pending_disposal: '#FB923C',
  archived: '#6B7280',
  destroyed: '#EF4444',
  deleted: '#9CA3AF',
};

const CLASSIFICATION_COLORS = [
  '#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444',
  '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16',
  '#06B6D4', '#D946EF', '#22D3EE', '#A855F7', '#FB7185',
];

const SENSITIVITY_COLORS: Record<string, string> = {
  None: '#10B981',
  Standard: '#F59E0B',
  Sensitive: '#EF4444',
};

const INFORMATION_SECURITY_CLASSIFICATION_COLORS: Record<string, string> = {
  Unclassified: '#10B981',
  'In-Confidence': '#F59E0B',
  Sensitive: '#EF4444',
  Restricted: '#7C3AED',
};

const MAORI_CATEGORY_COLORS: Record<string, string> = {
  'Iwi Relationships': '#8B5CF6',
  'Taonga Tuku Iho': '#14B8A6',
  'Environmental Kaitiakitanga': '#10B981',
  'Te Tiriti o Waitangi': '#3B82F6',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  final: 'Final',
  transitory: 'Transitory',
  pending_finalisation: 'Pending Finalisation',
  pending_disposal: 'Pending Disposal',
  archived: 'Archived',
  destroyed: 'Destroyed',
  deleted: 'Deleted',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(d: Date): string {
  return d.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', timeZone: 'Pacific/Auckland' });
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

const NZ_TZ = 'Pacific/Auckland';

function timeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days <= 7) return `${days}d ago`;
  return date.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', timeZone: NZ_TZ });
}

function statusDisplayName(status: string | null | undefined): string {
  const s = status == null ? '' : String(status);
  if (!s) return '—';
  return s
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Custom Tooltip for charts
// ---------------------------------------------------------------------------

const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((entry: any) => (
        <div key={entry.dataKey} className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-gray-600 capitalize">{entry.dataKey}:</span>
          <span className="font-medium text-gray-900">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// KPI Card
// ---------------------------------------------------------------------------

const KpiCard = ({
  title,
  value,
  icon: Icon,
  color,
  subtitle,
  trend,
}: {
  title: string;
  value: string;
  icon: React.ElementType;
  color: string;
  subtitle?: string;
  trend?: 'up' | 'down' | null;
}) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 hover:shadow-md transition-shadow duration-300">
    <div className="flex items-start justify-between">
      <div className={`p-3 rounded-xl ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      {trend === 'up' && <ArrowUpRight className="w-4 h-4 text-emerald-500" />}
      {trend === 'down' && <ArrowDownRight className="w-4 h-4 text-red-400" />}
    </div>
    <div className="mt-4">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500 mt-1">{title}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------

const Section = ({
  title,
  subtitle,
  children,
  className = '',
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-6 ${className}`}>
    <div className="mb-6">
      <h3 className="text-base font-bold text-gray-900">{title}</h3>
      {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// Donut center label
// ---------------------------------------------------------------------------

const DonutCenterLabel = ({ total }: { total: number }) => (
  <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central">
    <tspan x="50%" dy="-0.4em" className="text-2xl font-bold fill-gray-900">{formatNumber(total)}</tspan>
    <tspan x="50%" dy="1.5em" className="text-xs fill-gray-500">total</tspan>
  </text>
);

// ---------------------------------------------------------------------------
// Main Dashboard Component
// ---------------------------------------------------------------------------

export default function Dashboard() {
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [kpis, setKpis] = useState<KpiData | null>(null);
  const [dailyActivity, setDailyActivity] = useState<DailyActivity[]>([]);
  const [recordStatusDist, setRecordStatusDist] = useState<CategoryCount[]>([]);
  const [classificationDist, setClassificationDist] = useState<CategoryCount[]>([]);
  const [functionDist, setFunctionDist] = useState<CategoryCount[]>([]);
  const [sensitivityDist, setSensitivityDist] = useState<CategoryCount[]>([]);
  const [maoriCategoryDist, setMaoriCategoryDist] = useState<CategoryCount[]>([]);
  const [informationSecurityClassificationDist, setInformationSecurityClassificationDist] = useState<CategoryCount[]>([]);
  const [recentRecords, setRecentRecords] = useState<RecentRecord[]>([]);
  const [recentItems, setRecentItems] = useState<RecentItem[]>([]);

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const fetchDashboardMetrics = useCallback(async () => {
    const response = await apiFetch('/api/v1/dashboard/metrics');
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();

    setKpis(data.kpis);
    setDailyActivity(
      data.dailyActivity.map((d: DailyActivity & { date: string }) => ({
        ...d,
        date: formatDate(new Date(d.date + 'T00:00:00')),
      }))
    );
    setRecordStatusDist(data.recordStatusDistribution);
    setClassificationDist(data.classificationDistribution);
    setFunctionDist(data.functionDistribution);
    setSensitivityDist(data.sensitivityDistribution);
    setMaoriCategoryDist(data.maoriCategoryDistribution);
    setInformationSecurityClassificationDist(data.informationSecurityClassificationDistribution);
    setRecentRecords(
      data.recentRecords.map((r: RecentRecord & { createdAt: string }) => ({
        ...r,
        createdAt: new Date(r.createdAt),
      }))
    );
    setRecentItems(
      data.recentActivity.map((item: RecentItem & { initiatedAt: string }) => ({
        ...item,
        initiatedAt: new Date(item.initiatedAt),
      }))
    );
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await fetchDashboardMetrics();
      } catch (err) {
        console.error('Dashboard data fetch failed:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchDashboardMetrics]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchDashboardMetrics();
    } catch (err) {
      console.error('Refresh failed:', err);
    } finally {
      setRefreshing(false);
    }
  };

  // -------------------------------------------------------------------------
  // Status badge
  // -------------------------------------------------------------------------

  const StatusBadge = ({ status }: { status: string | null | undefined }) => {
    const colorMap: Record<string, string> = {
      confirmed: 'bg-emerald-100 text-emerald-800',
      completed: 'bg-purple-100 text-purple-800',
      in_progress: 'bg-blue-100 text-blue-800',
      initiated: 'bg-gray-100 text-gray-700',
      discarded: 'bg-red-100 text-red-800',
      failed: 'bg-amber-100 text-amber-800',
      draft: 'bg-blue-100 text-blue-800',
      final: 'bg-emerald-100 text-emerald-800',
      transitory: 'bg-amber-100 text-amber-800',
      pending_finalisation: 'bg-purple-100 text-purple-800',
      pending_disposal: 'bg-orange-100 text-orange-800',
      archived: 'bg-gray-100 text-gray-700',
      destroyed: 'bg-red-100 text-red-800',
      deleted: 'bg-gray-100 text-gray-500',
    };
    const key = status ?? '';
    return (
      <span className={`text-xs font-medium px-2.5 py-1 rounded-lg ${colorMap[key] || 'bg-gray-100 text-gray-700'}`}>
        {statusDisplayName(status)}
      </span>
    );
  };

  // -------------------------------------------------------------------------
  // Loading skeleton
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex-1 bg-white text-gray-900 font-sans">
        <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-20 items-center">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 ${BRAND_BG} rounded-xl shadow-lg shadow-emerald-200 flex items-center justify-center text-white`}>
                  <Database className="w-5 h-5" />
                </div>
                <div>
                  <span className={`text-xl font-bold tracking-tight ${BRAND_TEXT}`}>ARA</span>
                  <span className="text-xs text-gray-400 block font-medium -mt-1 tracking-widest opacity-60">Automated Records Assistant</span>
                </div>
              </div>
            </div>
          </div>
        </nav>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            <p className="text-gray-500 text-sm">Loading dashboard data...</p>
          </div>
        </main>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const recordStatusTotal = recordStatusDist.reduce((sum, s) => sum + s.value, 0);

  return (
    <div className="flex-1 bg-gray-50/50 text-gray-900 font-sans selection:bg-emerald-100 selection:text-emerald-900">
      {/* Navbar */}
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center">
            <div className="flex items-center space-x-3 group cursor-pointer" onClick={() => navigate('/')}>
              <div className={`w-10 h-10 ${BRAND_BG} rounded-xl shadow-lg shadow-emerald-200 flex items-center justify-center text-white transform group-hover:scale-110 transition-transform duration-300`}>
                <Database className="w-5 h-5" />
              </div>
              <div>
                <span className={`text-xl font-bold tracking-tight ${BRAND_TEXT}`}>ARA</span>
                <span className="text-xs text-gray-400 block font-medium -mt-1 tracking-widest opacity-60">Automated Records Assistant</span>
              </div>
            </div>
            <AppNavActions />
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Page header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-emerald-600" />
              Dashboard
            </h1>
            <p className="text-sm text-gray-500 mt-1">Overview of your records management activity</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* KPI Cards */}
        {kpis && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <KpiCard
              title="Total Records"
              value={formatNumber(kpis.totalRecords)}
              icon={Database}
              color="bg-emerald-100 text-emerald-700"
            />
            <KpiCard
              title="Ingested (30d)"
              value={formatNumber(kpis.confirmedLast30)}
              icon={TrendingUp}
              color="bg-blue-100 text-blue-700"
              subtitle="Last 30 days"
              trend={kpis.confirmedLast30 > 0 ? 'up' : null}
            />
            <KpiCard
              title="Pending Review"
              value={formatNumber(kpis.pendingReview)}
              icon={Clock}
              color="bg-purple-100 text-purple-700"
              subtitle="Awaiting confirmation"
            />
            <KpiCard
              title="Failed"
              value={formatNumber(kpis.failedIngestions)}
              icon={AlertTriangle}
              color="bg-amber-100 text-amber-700"
              subtitle="All time"
              trend={kpis.failedIngestions > 0 ? 'down' : null}
            />
            <KpiCard
              title="Discard Rate"
              value={`${kpis.discardRate.toFixed(1)}%`}
              icon={XCircle}
              color="bg-red-100 text-red-700"
              subtitle="Last 30 days"
            />
            <KpiCard
              title="PII Detected"
              value={formatNumber(kpis.piiDetected)}
              icon={Shield}
              color="bg-orange-100 text-orange-700"
              subtitle="Records with personal information"
            />
            <KpiCard
              title="Māori Data"
              value={formatNumber(kpis.maoriDataDetected)}
              icon={Globe2}
              color="bg-violet-100 text-violet-700"
              subtitle="Records with sovereignty considerations"
            />
            <KpiCard
              title="Restricted Access"
              value={formatNumber(kpis.restrictedAccess)}
              icon={Lock}
              color="bg-rose-100 text-rose-700"
              subtitle="In-confidence or sensitive"
            />
          </div>
        )}

        {/* Charts Row 1: Activity + Record Status */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Ingestion Activity */}
          <Section
            title="Ingestion Activity"
            subtitle="Daily document processing outcomes (last 30 days)"
            className="lg:col-span-2"
          >
            {dailyActivity.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dailyActivity} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: '#9CA3AF' }}
                    tickLine={false}
                    axisLine={{ stroke: '#E5E7EB' }}
                    interval={Math.max(0, Math.floor(dailyActivity.length / 8) - 1)}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#9CA3AF' }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <RechartsTooltip content={<ChartTooltip />} />
                  <Bar dataKey="confirmed" stackId="a" fill={STATUS_COLORS.confirmed} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="pending" stackId="a" fill={STATUS_COLORS.pending} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="discarded" stackId="a" fill={STATUS_COLORS.discarded} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="failed" stackId="a" fill={STATUS_COLORS.failed} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-gray-400 text-sm">
                No ingestion activity in the last 30 days
              </div>
            )}
          </Section>

          {/* Record Status Distribution */}
          <Section title="Record Status" subtitle="Distribution across lifecycle states">
            {recordStatusDist.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={recordStatusDist}
                    cx="50%"
                    cy="50%"
                    innerRadius={65}
                    outerRadius={95}
                    paddingAngle={3}
                    dataKey="value"
                    stroke="none"
                  >
                    {recordStatusDist.map((entry, i) => {
                      const statusKey = Object.entries(STATUS_LABELS).find(([, v]) => v === entry.name)?.[0] || '';
                      return <Cell key={entry.name} fill={STATUS_COLORS[statusKey] || CLASSIFICATION_COLORS[i % CLASSIFICATION_COLORS.length]} />;
                    })}
                  </Pie>
                  <DonutCenterLabel total={recordStatusTotal} />
                  <RechartsTooltip content={<ChartTooltip />} />
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: '12px', paddingTop: '12px' }}
                    formatter={(value: string) => <span className="text-gray-600 ml-1">{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-gray-400 text-sm">
                No records found
              </div>
            )}
          </Section>
        </div>

        {/* Charts Row 2: Classification + Function */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Record Classification Distribution */}
          <Section title="Record Classification" subtitle="Records by top-level classification category">
            {classificationDist.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(250, classificationDist.length * 40 + 30)}>
                <BarChart data={classificationDist} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fill: '#9CA3AF' }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11, fill: '#6B7280' }}
                    tickLine={false}
                    axisLine={false}
                    width={140}
                  />
                  <RechartsTooltip content={<ChartTooltip />} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={20}>
                    {classificationDist.map((_, i) => (
                      <Cell key={i} fill={CLASSIFICATION_COLORS[i % CLASSIFICATION_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-gray-400 text-sm">
                No classified records found
              </div>
            )}
          </Section>

          {/* Function Classification Distribution */}
          <Section title="Function Classification" subtitle="Records by business function domain">
            {functionDist.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(250, functionDist.length * 40 + 30)}>
                <BarChart data={functionDist} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fill: '#9CA3AF' }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11, fill: '#6B7280' }}
                    tickLine={false}
                    axisLine={false}
                    width={160}
                  />
                  <RechartsTooltip content={<ChartTooltip />} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={20}>
                    {functionDist.map((_, i) => (
                      <Cell key={i} fill={CLASSIFICATION_COLORS[(i + 5) % CLASSIFICATION_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-gray-400 text-sm">
                No function-classified records found
              </div>
            )}
          </Section>
        </div>

        {/* Charts Row 3: PII Sensitivity + Security Classification + Māori Category */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <Section title="PII Sensitivity" subtitle="Records by personal information sensitivity">
            {sensitivityDist.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={sensitivityDist}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                    stroke="none"
                  >
                    {sensitivityDist.map((entry) => (
                      <Cell key={entry.name} fill={SENSITIVITY_COLORS[entry.name] || '#9CA3AF'} />
                    ))}
                  </Pie>
                  <DonutCenterLabel total={sensitivityDist.reduce((s, e) => s + e.value, 0)} />
                  <RechartsTooltip content={<ChartTooltip />} />
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }}
                    formatter={(value: string) => <span className="text-gray-600 ml-1">{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[260px] text-gray-400 text-sm">
                No PII data available
              </div>
            )}
          </Section>

          <Section title="Security Classification" subtitle="Records by information security classification">
            {informationSecurityClassificationDist.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={informationSecurityClassificationDist}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                    stroke="none"
                  >
                    {informationSecurityClassificationDist.map((entry) => (
                      <Cell key={entry.name} fill={INFORMATION_SECURITY_CLASSIFICATION_COLORS[entry.name] || '#9CA3AF'} />
                    ))}
                  </Pie>
                  <DonutCenterLabel total={informationSecurityClassificationDist.reduce((s, e) => s + e.value, 0)} />
                  <RechartsTooltip content={<ChartTooltip />} />
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }}
                    formatter={(value: string) => <span className="text-gray-600 ml-1">{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[260px] text-gray-400 text-sm">
                No information security classification data available
              </div>
            )}
          </Section>

          <Section title="Māori Data Sovereignty" subtitle="Records by sovereignty category">
            {maoriCategoryDist.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={maoriCategoryDist} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fill: '#9CA3AF' }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 10, fill: '#6B7280' }}
                    tickLine={false}
                    axisLine={false}
                    width={130}
                  />
                  <RechartsTooltip content={<ChartTooltip />} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={18}>
                    {maoriCategoryDist.map((entry) => (
                      <Cell key={entry.name} fill={MAORI_CATEGORY_COLORS[entry.name] || '#8B5CF6'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[260px] text-gray-400 text-sm">
                No Māori data sovereignty records found
              </div>
            )}
          </Section>
        </div>

        {/* Recent Records Table */}
        <Section title="Recent Records" subtitle="Latest records added to the system" className="mb-6">
          {recentRecords.length > 0 ? (
            <div className="overflow-x-auto -mx-6 px-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-3 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Document</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Status</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">PII</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Access</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Māori Data</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Size</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">When</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRecords.map((record) => (
                    <tr
                      key={record.id}
                      className="border-b border-gray-50 hover:bg-emerald-50/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/records/${record.id}`)}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-gray-100 rounded-lg">
                            <FileText className="w-4 h-4 text-gray-500" />
                          </div>
                          <span className="font-medium text-gray-900 truncate max-w-[200px]" title={record.title}>
                            {record.title}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <StatusBadge status={record.status} />
                      </td>
                      <td className="py-3 px-4">
                        {record.piiDetected ? (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-lg ${record.sensitivityLevel === 'sensitive' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                            {statusDisplayName(record.sensitivityLevel)}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">None</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <IscBadge level={record.informationSecurityClassification} />
                      </td>
                      <td className="py-3 px-4">
                        {record.maoriDataDetected ? (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-lg bg-violet-100 text-violet-700">Yes</span>
                        ) : (
                          <span className="text-xs text-gray-400">No</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-500">{formatBytes(record.fileSize)}</td>
                      <td className="py-3 px-4 text-right text-gray-400">{timeAgo(record.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400 text-sm">
              No records found
            </div>
          )}
        </Section>

        {/* Recent Ingestion Activity Table */}
        <Section title="Recent Ingestion Activity" subtitle="Latest document processing events">
          {recentItems.length > 0 ? (
            <div className="overflow-x-auto -mx-6 px-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-3 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Document</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Status</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Size</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Type</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">When</th>
                  </tr>
                </thead>
                <tbody>
                  {recentItems.map((item) => (
                    <tr key={item.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-gray-100 rounded-lg">
                            <FileText className="w-4 h-4 text-gray-500" />
                          </div>
                          <span className="font-medium text-gray-900 truncate max-w-[240px]" title={item.fileName}>
                            {item.fileName}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <StatusBadge status={item.status} />
                      </td>
                      <td className="py-3 px-4 text-gray-500">{formatBytes(item.fileSize)}</td>
                      <td className="py-3 px-4 text-gray-500 font-mono text-xs">{item.contentType || '—'}</td>
                      <td className="py-3 px-4 text-right text-gray-400">{timeAgo(item.initiatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400 text-sm">
              No recent ingestion activity
            </div>
          )}
        </Section>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-gray-500">
          <p>&copy; 2026 Palmerston North City Council &bull; ARA</p>
        </div>
      </footer>
    </div>
  );
}
